import type { ContactState } from '../enums';
import type { ProjectCommercial } from './admin';
import type { CustomFieldDto, CustomValues, LeadTagDto, TagDto } from './customization';
/**
 * Tipos de respuesta de la API consumidos por la web. No son Zod: los produce el servidor.
 */
import type { LeadAttention } from './attention';
import type {
  DealType,
  FinancingType,
  InteractionOrigin,
  InteractionResult,
  InteractionStatus,
  InteractionType,
  InterestLevel,
  LeadHistoryKind,
  LeadOriginType,
  LeadStatus,
  PipelineStage,
  Role,
  UnitType,
  AppointmentMode,
  AppointmentPlatform,
} from '../enums';
import type { FollowupState, OverdueBand } from '../funnel/followup';
import type { ContactConsent, LeadAttribution, PurchaseProfile } from './commercial';
import type { CompanySettings } from './companySettings';
import type { LeadListItem } from './leads';

export interface InteractionItem {
  id: string;
  leadId: string;
  type: InteractionType;
  status: InteractionStatus;
  origin: InteractionOrigin;
  scheduledAt: string | null;
  completedAt: string | null;
  visitAt: string | null;
  result: InteractionResult | null;
  nonEffectiveReason: string | null;
  description: string | null;
  advisorId: string | null;
  advisorName: string | null;
  createdById: string | null;
  createdByName: string | null;
  createdByKind: 'HUMAN' | 'BOT' | null;
  unitCode: string | null;
  /** Modalidad de la cita (solo SE_CITO). */
  appointment: AppointmentInfo | null;
  /** Nació de una respuesta simulada del cliente: se ve, pero no cuenta en el embudo ni en los contadores. */
  simulated: boolean;
  /** Quien mira puede anular esta visita (registrada por error, dentro de las 24 h, propia o gestiona). */
  canAnnul: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/**
 * Respuesta de una acción sobre una tarea cuando el lead sale del alcance de quien la hizo (p. ej. el asesor digital
 * completa la doble confirmación y el lead pasa al asesor de sala): la acción se guardó, pero no hay ficha que devolver.
 */
export interface LeadActionNotice {
  outOfScope: true;
  leadId: string;
  message: string;
}
export type LeadActionResult = LeadDetail | LeadActionNotice;
export function isLeadNotice(r: LeadActionResult): r is LeadActionNotice {
  return (r as LeadActionNotice).outOfScope === true;
}

export interface AppointmentInfo {
  mode: AppointmentMode;
  platform: AppointmentPlatform | null;
  location: string | null;
  url: string | null;
  /** 7.22: asesor de sala (null = el asesor del lead) y confirmaciones de la cita concretada. */
  roomAdvisorId: string | null;
  roomAdvisorName: string | null;
  confirmations: AppointmentConfirmations;
}

export interface ConfirmationMark {
  at: string;
  byId: string | null;
  byName: string | null;
}

export interface AppointmentConfirmations {
  /** true = la cita la confirman dos personas (asesor digital y asesor de sala distintos). */
  needsDouble: boolean;
  digital: ConfirmationMark | null;
  room: ConfirmationMark | null;
  /** Qué puede marcar quien mira la ficha (su propio rol, o ambos si gestiona). */
  canConfirm: { digital: boolean; room: boolean };
}

export interface LeadHistoryItem {
  id: string;
  kind: LeadHistoryKind;
  fromValue: string | null;
  toValue: string | null;
  /** Para los cambios de asesor (`ASESOR`): nombres de los usuarios que hay detrás de los ids (G2). */
  fromName: string | null;
  toName: string | null;
  reason: string | null;
  changedByName: string | null;
  createdAt: string;
}

export interface ScoreHistoryItem {
  id: string;
  fromScore: number;
  toScore: number;
  reason: string | null;
  actorType: 'USER' | 'BOT' | 'SYSTEM';
  actorName: string | null;
  createdAt: string;
}

export interface QuoteItem {
  id: string;
  code: string;
  priceList: string | null;
  discount: string;
  price: string;
  currency: string;
  financingType: FinancingType | null;
  status: string;
  unitId: string | null;
  unitCode: string | null;
  unitType: UnitType | null;
  notes: string | null;
  annulledAt: string | null;
  annulReason: string | null;
  convertedAt: string | null;
  createdByName: string | null;
  createdAt: string;
  expiresAt: string | null;
}

export interface DealItem {
  id: string;
  type: DealType;
  quoteCode: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  minuteSignedAt: string | null;
  refundedAt: string | null;
  dropReason: string | null;
  refundNotes: string | null;
  annulledAt: string | null;
  annulReason: string | null;
  contractMode: FinancingType | null;
  bank: string | null;
  contractNumber: string | null;
  amountTotal: string | null;
  amountInitial: string | null;
  /** Moneda del precio (la de la unidad). */
  currency: string;
  /** Moneda de la reserva / inicial (`amountInitial`), propia desde el Lote 5 · H2 (P9). */
  initialCurrency: string;
  /** `price` = precio pactado por unidad (el de la proforma convertida o el de la unidad al registrar). */
  units: Array<{ id: string; code: string; unitType: UnitType; price: string | null }>;
  advisorName: string | null;
  state: 'ACTIVO' | 'DEVUELTO' | 'ANULADO';
}

export interface DuplicateContact {
  contactId: string;
  name: string;
  phone: string | null;
  email: string | null;
  documentNumber: string | null;
  matchedOn: Array<'TELEFONO' | 'CORREO' | 'DOCUMENTO'>;
  leads: Array<{
    id: string;
    projectName: string;
    status: LeadStatus;
    pipelineStage: PipelineStage;
    capturedAt: string;
  }>;
}

export interface LeadFollowup {
  state: FollowupState;
  band: OverdueBand | null;
  days: number;
  nextTaskAt: string | null;
  referenceAt: string | null;
}

export interface LeadSla {
  minutes: number | null;
  elapsedMinutes: number;
  withinSla: boolean;
  slaMinutes: number;
}

export interface LeadDetail {
  id: string;
  companyId: string;
  companyName: string;
  projectId: string;
  projectName: string;
  contact: {
    id: string;
    firstName: string;
    lastNamePaternal: string | null;
    lastNameMaternal: string | null;
    fullName: string;
    phone: string | null;
    phoneNormalized: string | null;
    email: string | null;
    documentType: string | null;
    documentNumber: string | null;
    district: string | null;
    province: string | null;
    department: string | null;
    occupation: string | null;
    birthDate: string | null;
    isTest: boolean;
    consent: ContactConsent;
    /** Celulares, correos y documentos alternos de la misma persona (otros registros o fusiones), normalizados. */
    altPhones: string[];
    altEmails: string[];
    altDocuments: string[];
  };
  originType: LeadOriginType;
  capturedAt: string;
  medium: string | null;
  mediumCategory: string | null;
  originalMedium: string | null;
  originalMediumCategory: string | null;
  entryChannel: string | null;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    term: string | null;
    content: string | null;
  };
  status: LeadStatus;
  subStatus: string | null;
  pipelineStage: PipelineStage;
  processStatus: string;
  interestLevel: InterestLevel;
  score: number;
  advisorId: string | null;
  advisorName: string | null;
  /** Lote 6 · I1: cuándo llegó el lead a su asesor actual (null en la bolsa); desde ahí cuenta la reasignación automática. */
  advisorAssignedAt: string | null;
  /** 7.22: asesor que captó el lead antes de pasarlo al asesor de sala (atribución en espera). */
  captureAdvisorId: string | null;
  captureAdvisorName: string | null;
  /**
   * Lote 5 · H1 (P18): true cuando quien mira ya no tiene el lead en su alcance y lo ve solo por haberlo captado
   * (asesor de captación tras el traspaso al asesor de sala). La ficha se muestra sin acciones.
   */
  readOnly: boolean;
  /** 7.26: contacto del lead. */
  contactState: ContactState;
  contactAttempts: number;
  lastNonEffectiveReason: string | null;
  lastNonEffectiveReasonLabel: string | null;
  hasDesisted: boolean;
  desistReason: string | null;
  desistedAt: string | null;
  /** Atención por bot (null = no lo atiende el bot). */
  attention: LeadAttention | null;
  /** Solo en la ficha: quién pausó al bot y cuándo retoma si el asesor no registra nada. */
  attentionInfo: {
    pausedByName: string | null;
    resumeAfterMinutes: number;
    /** Momento en que el bot retoma (pausedAt + resumeAfterMinutes); null si el bot no está en pausa. */
    resumeAt: string | null;
    /** Con el bot apagado en la empresa no escribe, no responde ni retoma: el lead sigue con su asesor. */
    botActive: boolean;
  } | null;
  isUniqueLeadMonth: boolean;
  isRecontact: boolean;
  /** G1: última vez que la persona volvió a registrarse sobre este lead. */
  lastRecontactAt: string | null;
  externalIds: Record<string, unknown>;
  attribution: Partial<LeadAttribution>;
  profile: PurchaseProfile;
  followup: LeadFollowup;
  sla: LeadSla;
  nextTask: InteractionItem | null;
  interactions: InteractionItem[];
  quotes: QuoteItem[];
  deals: DealItem[];
  history: LeadHistoryItem[];
  scoreHistory: ScoreHistoryItem[];
  /** Otros leads del mismo contacto (otros proyectos o meses). */
  contactLeads: Array<{
    id: string;
    projectName: string;
    status: LeadStatus;
    pipelineStage: PipelineStage;
    capturedAt: string;
    medium: string | null;
  }>;
  /** Otros contactos de la empresa que comparten teléfono, correo o documento. */
  duplicates: DuplicateContact[];
  tags: LeadTagDto[];
  /** Valores de los campos personalizados de la empresa (`{ clave: valor }`). */
  custom: CustomValues;
  createdAt: string;
  updatedAt: string;
}

export interface MyDayTask {
  id: string;
  type: InteractionType;
  scheduledAt: string;
  description: string | null;
  appointment: AppointmentInfo | null;
  isHandoff: boolean;
  advisorName: string | null;
  lead: {
    id: string;
    companyId: string;
    contactName: string;
    phone: string | null;
    phoneNormalized: string | null;
    /** false = pidió no ser contactado (`optOutAt`): Llamar y WhatsApp se deshabilitan en Mi día. */
    contactable: boolean;
    optOutAt: string | null;
    projectName: string;
    pipelineStage: PipelineStage;
    subStatus: string | null;
    score: number;
    interestLevel: InterestLevel;
  };
}

export interface MyDayNewLead extends LeadListItem {
  minutesSinceCapture: number;
  slaElapsedMinutes: number;
  withinSla: boolean;
}

export interface MyDayResponse {
  advisorId: string;
  advisorName: string;
  overdue: MyDayTask[];
  today: MyDayTask[];
  upcoming: MyDayTask[];
  newLeads: MyDayNewLead[];
  /** Leads del asesor que el bot marcó como prioridad y sigue atendiendo (Fase 8). */
  botPriority: LeadListItem[];
  /** 7.25: tareas del día o rango elegido en el calendario (`from` / `to`, fechas YYYY-MM-DD); null sin filtro. */
  range: { from: string; to: string; tasks: MyDayTask[] } | null;
  /**
   * Contadores reales (`count()` aparte de las listas, sin tope). Las listas de arriba se recortan a
   * `MY_DAY_TASK_LIMIT` / `MY_DAY_NEW_LEADS_LIMIT`: cuando `overdue.length < counts.overdue` la web dice «200 de 350».
   */
  counts: {
    overdue: number;
    today: number;
    upcoming: number;
    newLeads: number;
    botPriority: number;
    /** Leads VENCIDOS del asesor por banda (días calendario en la zona de la empresa, `overdueBandRange`). */
    leadsVencidos: number;
    leadsVencidosHoy: number;
    leadsVencidosAyer: number;
    leadsVencidos2a7: number;
    leadsVencidosMas7: number;
    leadsAlDia: number;
  };
}

export interface CatalogItemDto {
  code: string;
  label: string;
  group: string | null;
  sortOrder: number;
  metadata: Record<string, unknown>;
}

export interface CatalogsResponse {
  companyId: string;
  subStatuses: CatalogItemDto[];
  desistReasons: CatalogItemDto[];
  nonEffectiveReasons: CatalogItemDto[];
  entryChannels: CatalogItemDto[];
  dropReasons: CatalogItemDto[];
  mediumCategories: Array<{ code: string; name: string; channel: string; isDigital: boolean }>;
  media: Array<{ name: string; categoryCode: string }>;
  /** Etiquetas activas de la empresa. */
  tags: TagDto[];
  /** Campos personalizados activos, en orden. */
  customFields: CustomFieldDto[];
  settings: Pick<
    CompanySettings,
    | 'firstContactSlaMinutes'
    | 'businessHours'
    | 'semaphore'
    | 'digitalCategories'
    | 'timezone'
    | 'currency'
    | 'penPerUsd'
    | 'countableUnitTypes'
    | 'requirePriorSeparationForSale'
  >;
}

export interface ProjectOption {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  district: string | null;
  address: string | null;
  active: boolean;
  /** Ficha comercial (separación, inicial, financiamiento…) para prellenar diálogos. */
  commercial: ProjectCommercial;
}

/** G1: a quién le tocaría el próximo lead del proyecto por turnos (sin consumir el turno). */
export interface NextAdvisorDto {
  /** true = el proyecto tiene regla de reparto por turnos activa con asesores. */
  active: boolean;
  advisorId: string | null;
  advisorName: string | null;
}

export interface AdvisorOption {
  id: string;
  fullName: string;
  role: Role;
  advisorFunction: string | null;
  kind: 'HUMAN' | 'BOT';
  /** Cuenta de prueba (excluida de métricas): no debe recibir leads reales por reparto automático. */
  isTestAccount: boolean;
  /** false = dado de baja (solo llega con `includeInactive=1`, para ubicar y reasignar sus leads). */
  active: boolean;
  /**
   * Lote 6 · I2: si hoy puede recibir leads (activo, humano, con rol de ventas y, si se consultó con `projectId`,
   * con acceso a ese proyecto). Con `includeInactive=1` la lista trae también a quienes no pueden, con el motivo.
   */
  assignable: boolean;
  /** «dado de baja», «sin acceso al proyecto» o «sin rol de ventas»; null cuando es asignable. */
  inactiveReason: string | null;
}

export interface ApiKeyDto {
  id: string;
  name: string;
  companyId: string;
  keyPrefix: string;
  userId: string;
  active: boolean;
  scopes: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}
